import React from 'react';
import { Stack, Switch, Typography } from '@mui/material';
import { VerifiedUser as VerifiedUserIcon, Lock as LockIcon } from '@mui/icons-material';
import styles from './CustomerOrgs.module.scss';

export type AdminRole = 'full' | 'readonly';

interface RoleBannerProps {
  /** Whether the signed-in user has real super_user permission at all. */
  canWrite: boolean;
  /**
   * Client-side "arm before you act" switch — always starts locked on load, even for
   * super_users. It's a speed bump against fat-fingered edits, not a security boundary:
   * real authorization is still `canWrite` (user.super_user), enforced server-side too.
   */
  writeArmed: boolean;
  onToggleWriteArmed: (armed: boolean) => void;
}

export function RoleBanner({ canWrite, writeArmed, onToggleWriteArmed }: RoleBannerProps) {
  if (!canWrite) {
    return (
      <Stack direction="row" alignItems="center" spacing={1.5} className={styles.roleBannerLocked}>
        <LockIcon color="primary" />
        <Typography variant="body2">
          <strong>Read-only — Support view</strong> — You can browse orgs. Write actions are
          disabled.
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={1.5}
      className={writeArmed ? styles.roleBannerArmed : styles.roleBannerLocked}
    >
      <Stack direction="row" alignItems="center" spacing={1.5}>
        {writeArmed ? <VerifiedUserIcon color="warning" /> : <LockIcon color="primary" />}
        <Typography variant="body2">
          {writeArmed ? (
            <>
              <strong>Edits enabled</strong> — You can create, modify plans, extend trials, and
              manage org lifecycle.
            </>
          ) : (
            <>
              <strong>Read-only (default)</strong> — Flip the switch to enable edits.
            </>
          )}
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="body3" color="text.secondary">
          Enable edits
        </Typography>
        <Switch
          checked={writeArmed}
          onChange={(event) => onToggleWriteArmed(event.target.checked)}
          color={writeArmed ? 'warning' : 'primary'}
        />
      </Stack>
    </Stack>
  );
}
